// Fixed NavbarItem.js - Cleaned up version
import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  IconButton,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  useTheme
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { navbarItemStyles } from './NavbarItem.styles';
import { useNavbarItemActions } from './NavbarItemActions';
import { renderIcon } from "../../../helpers/renderDynamicIcons";
import { useTranslation } from "react-i18next";

const NavbarItem = ({
  item,
  level = 0,
  onItemClick,
  defaultExpanded = false,
  horizontal = false,
  horizontalTreeMode = false,
  onMenuClose = null,
  flatNavigationItems = []
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const theme = useTheme();
  
  const styles = navbarItemStyles(level);
  // Check if this item is active based on current route
  const isActive = location.pathname === item.to ||
    location.pathname.startsWith(item.to + '/');
  // Get actual children array - needed for rendering
  const getChildren = () => {
    if (item.children && Array.isArray(item.children)) {
      return item.children;
    }
    if (item.hasChildren && flatNavigationItems.length > 0) {
      return flatNavigationItems.filter(child => child.parentId === item.id);
    }
    return [];
  };

  const children = getChildren();

  // Get hooks - hasChildren is now calculated in the hook
  const {
    handleToggle,
    handleItemClick,
    getIcon,
    hasChildren
  } = useNavbarItemActions({
    item,
    isExpanded,
    setIsExpanded,
    onItemClick: (clickedItem) => {
      // This will only be called for leaf nodes now
      onItemClick(clickedItem);
      if (onMenuClose) {
        onMenuClose();
      }
    }
  });

  const isMenuOpen = Boolean(anchorEl);

  // Horizontal Tree Mode - For expandable navigation in app bar
  if (horizontalTreeMode) {
    const handleClick = (event) => {
      if (hasChildren) {
        setAnchorEl(event.currentTarget);
        event.currentTarget.blur();
      } else {
        handleItemClick();
      }
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        {/* <Button
          startIcon={renderIcon(item.icon)}
          endIcon={hasChildren ? (isMenuOpen ? <ExpandLess /> : <ExpandMore />) : null}
          onClick={handleClick}
          color="primary"
          disableRipple
          sx={{
            textTransform: 'none',
            position: 'relative',
            '&:hover': {
              backgroundColor: '#3d3c3e80',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#3d3c3e80',
              }
            },
            '&:focus': {
              outline: 'none'
            },
            // Apply active styles when isActive is true
            ...(isActive && {
              backgroundColor: 'rgba(29, 131, 84, 0.5)',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#1d8354',
              }
            })
          }}
        >
          {t(item.name)}
        </Button> */}

        <Button
          startIcon={renderIcon(item.icon)}
          endIcon={hasChildren ? (isMenuOpen ? <ExpandLess /> : <ExpandMore />) : null}
          onClick={handleClick}
          color="primary"
          disableRipple
          sx={{
            textTransform: 'none',
            position: 'relative',
            height: '100%',
            borderRadius: 0,
            '&:hover::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '75%', // Only cover bottom 75%
              backgroundColor: '#3e3c3c80',
              borderRadius: '2px 2px 0 0',
            },
            '&:hover::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '3px',
              backgroundColor: '#3d3c3e80',
            },
            '&:focus': {
              outline: 'none'
            },
            // Apply active styles when isActive is true
            ...(isActive && {
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '75%', // Only cover bottom 75%
                backgroundColor: 'rgba(29, 131, 84, 0.5)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: '#1d8354',
              }
            })
          }}
        >
          {t(item.name)}
        </Button>
        {hasChildren && (
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            disableRestoreFocus
            autoFocus={false}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200
              }
            }}
            MenuListProps={{
              'aria-label': `${item.name} submenu`,
              disablePadding: false
            }}
            slotProps={{
              backdrop: {
                invisible: true  // Makes the backdrop transparent and removes aria-hidden issues
              }
            }}
          >
            {children.map((child, index) => {
              const childHasChildren = child.children ?
                child.children.length > 0 :
                child.hasChildren || false;

              if (childHasChildren) {
                // Render child as a nested NavbarItem
                return (
                  <Box key={child.id || index} sx={{ width: '100%' }}>
                    <NavbarItem
                      item={child}
                      onItemClick={onItemClick}
                      horizontalTreeMode={false}
                      level={level + 1}
                      onMenuClose={handleClose}
                      flatNavigationItems={flatNavigationItems}
                    />
                  </Box>
                );
              } else {
                // Render as simple MenuItem for leaf nodes
                return (
                  <MenuItem
                    key={child.id || index}
                    onClick={() => {
                      onItemClick(child);
                      handleClose();
                    }}
                    sx={{ py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {renderIcon(child.icon)}
                    </ListItemIcon>
                    <ListItemText primary={child.name} />
                  </MenuItem>
                );
              }
            })}
          </Menu>
        )}
      </>
    );
  }

  // Original Vertical Mode - For sidebar navigation
  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleItemClick}
          sx={styles.listItemButton}
        >
          <ListItemIcon sx={styles.listItemIcon}>
            {renderIcon(item.icon)}
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  ...styles.typography,
                  textAlign: theme.direction === 'rtl' ? 'right' : 'left',
                  width: '100%',
                }}
              >
                {t(item.name)}
              </Typography>
            }
            sx={{
              textAlign: theme.direction === 'rtl' ? 'right' : 'left',
              m: 0,
              flex: '1 1 auto',
            }}
          />

          {hasChildren && (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={styles.expandButton}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItemButton>
      </ListItem>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child, index) => (
              <NavbarItem
                key={child.id || index}
                item={child}
                level={level + 1}
                onItemClick={onItemClick}
                defaultExpanded={child.defaultExpanded}
                horizontal={false}
                horizontalTreeMode={false}
                onMenuClose={onMenuClose}
                flatNavigationItems={flatNavigationItems}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default NavbarItem;